import GuidebookTextBlock from "../GuidebookTextBlock";
import type { GuidebookCollapseMode } from "../../types/guidebook";
import { getGuidebookContentHtml } from "../../utils/guidebookText";
import React from "react";
import {
  AlertTriangle,
  ExternalLink,
  FileText,
  Home,
  Image,
  KeyRound,
  Link,
  Menu,
  MapPin,
  Type,
  Video,
  Wifi,
  Wrench,
} from "lucide-react";
import assets from "../../assets";
import * as S from "./styles";

export interface GuidebookStyleFormData {
  backgroundColor?: string;
  fontColor?: string;
  fontFamily?: string;
  headingFontSize?: string;
  headingFontWeight?: string;
  textFontSize?: string;
  textFontWeight?: string;
}

export interface GuidebookViewerSection {
  id: string;
  title: string;
  content: string[];
  collapseMode?: GuidebookCollapseMode;
  images?: Array<
    | string
    | {
        src: string;
        fallbackSrcs?: string[];
      }
  >;
  externalVideoUrls?: string[];
  links?: Array<{ title: string; url: string }>;
  pdfs?: Array<{ url: string; originalName: string }>;
  iconType?: string;
  styleOverrides?: GuidebookStyleFormData;
}

interface GuidebookViewerProps {
  title: string;
  sections: GuidebookViewerSection[];
  globalStyles: GuidebookStyleFormData;
  sectionStyles?: Record<string, GuidebookStyleFormData>;
  shortcodeValues?: Record<string, string | null | undefined>;
  contextLabel?: string | null;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  forceMobileNav?: boolean;
}

const SECTION_ICON_BY_TYPE = {
  welcome: Home,
  wifi: Wifi,
  checkin: KeyRound,
  rules: FileText,
  appliances: Wrench,
  emergencies: AlertTriangle,
  "local-spots": MapPin,
  image: Image,
  video: Video,
  pdf: FileText,
  link: Link,
  "text-block": Type,
} as const;

const escapeHtml = (value: string) => {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

const normalizeToken = (value: string): string => {
  return value
    .trim()
    .replace(/[{}]/g, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[\s-]+/g, "_")
    .toLowerCase();
};

const sanitizeAnchorId = (value: string): string => {
  const normalized = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return normalized || "section";
};

const sanitizeHtml = (html: string): string => {
  if (typeof window === "undefined") {
    return html;
  }

  const parser = new DOMParser();
  const parsed = parser.parseFromString(html, "text/html");
  const allowedTags = new Set([
    "a",
    "p",
    "br",
    "strong",
    "em",
    "ul",
    "ol",
    "li",
    "blockquote",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "span",
    "img",
    "iframe",
  ]);
  const allowedGlobalAttributes = new Set(["style", "data-guidebook-shortcode"]);
  const allowedAttributesByTag: Record<string, Set<string>> = {
    a: new Set(["href", "target", "rel", "style", "data-guidebook-shortcode"]),
    img: new Set(["src", "alt", "title", "style", "data-guidebook-shortcode"]),
    iframe: new Set([
      "src",
      "title",
      "allow",
      "allowfullscreen",
      "frameborder",
      "scrolling",
      "style",
      "data-guidebook-shortcode",
    ]),
  };

  const nodes = Array.from(parsed.body.querySelectorAll("*"));
  nodes.reverse().forEach((node) => {
    const tag = node.tagName.toLowerCase();
    if (!allowedTags.has(tag)) {
      const parent = node.parentNode;
      if (!parent) {
        node.remove();
        return;
      }
      while (node.firstChild) parent.insertBefore(node.firstChild, node);
      parent.removeChild(node);
      return;
    }

    Array.from(node.attributes).forEach((attribute) => {
      const attributeName = attribute.name.toLowerCase();
      const tagAllowedAttributes =
        allowedAttributesByTag[tag] || allowedGlobalAttributes;
      const isAllowed =
        allowedGlobalAttributes.has(attributeName) ||
        tagAllowedAttributes.has(attributeName);

      if (attributeName.startsWith("on") || !isAllowed) {
        node.removeAttribute(attribute.name);
        return;
      }

      if (
        (attributeName === "href" || attributeName === "src") &&
        /^\s*javascript:/i.test(attribute.value)
      ) {
        node.removeAttribute(attribute.name);
      }
    });
  });

  return parsed.body.innerHTML;
};

const resolveShortcodes = (
  content: string,
  values: Record<string, string | null | undefined>
) => {
  return content.replace(/\{{\s*([^}]+?)\s*\}}/g, (_, rawToken: string) => {
    const token = normalizeToken(rawToken);
    const resolved = values[token];
    if (typeof resolved === "string" && resolved.trim().length > 0) {
      return escapeHtml(resolved);
    }

    return `<span data-guidebook-shortcode="${escapeHtml(
      token
    )}" style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:999px;background:#eef2ff;color:#4338ca;font-size:0.85em;font-weight:600;white-space:normal;">${escapeHtml(
      token.replace(/_/g, " ")
    )}</span>`;
  });
};

const isYouTubeEmbedUrl = (url: string): boolean => {
  return url.includes("youtube.com/embed/");
};

const buildMediaCandidates = (
  source: string,
  fallbackSources: string[] = []
): string[] => {
  return Array.from(new Set([source, ...fallbackSources].filter(Boolean)));
};

const GuidebookMediaImage: React.FC<{
  src: string;
  alt: string;
  fallbackSrcs?: string[];
}> = ({ src, alt, fallbackSrcs = [] }) => {
  const candidates = React.useMemo(
    () => buildMediaCandidates(src, fallbackSrcs),
    [src, fallbackSrcs]
  );
  const [candidateIndex, setCandidateIndex] = React.useState(0);
  const [usePlaceholder, setUsePlaceholder] = React.useState(false);

  React.useEffect(() => {
    setCandidateIndex(0);
    setUsePlaceholder(false);
  }, [src]);

  const handleError = React.useCallback(() => {
    setCandidateIndex((current) => {
      if (current < candidates.length - 1) {
        return current + 1;
      }
      setUsePlaceholder(true);
      return current;
    });
  }, [candidates.length]);

  return (
    <S.MediaImage
      src={usePlaceholder ? assets.imagePlaceholderWhite : candidates[candidateIndex] || src}
      alt={alt}
      loading="lazy"
      onError={handleError}
    />
  );
};

const mergeWithDefined = <T extends object>(
  base: T,
  overrides?: Partial<T>
): T => {
  if (!overrides) return base;

  const cleanOverrides = Object.fromEntries(
    Object.entries(overrides).filter(([, value]) => value !== undefined)
  ) as Partial<T>;

  return {
    ...base,
    ...cleanOverrides,
  };
};

const loadGuidebookFont = (fontFamily?: string) => {
  if (typeof document === "undefined" || !fontFamily) return;

  const fontName = fontFamily
    .split(",")[0]
    .replace(/["']/g, "")
    .trim();
  if (!fontName || /^(serif|sans-serif|monospace)$/i.test(fontName)) return;
  if (document.querySelector(`link[data-guidebook-font="${fontName}"]`)) return;

  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(
    /\s+/g,
    "+"
  )}:wght@400;500;600;700&display=swap`;
  link.setAttribute("data-guidebook-font", fontName);
  document.head.appendChild(link);
};

const GuidebookViewer: React.FC<GuidebookViewerProps> = ({
  title,
  sections,
  globalStyles,
  sectionStyles = {},
  shortcodeValues = {},
  contextLabel,
  scrollContainerRef,
  forceMobileNav = false,
}) => {
  const navigableSections = React.useMemo(
    () =>
      sections
        .map((section, originalIndex) => ({ ...section, originalIndex }))
        .filter((section) => section.title !== "Divider")
        .map((section, navIndex) => ({
          ...section,
          navKey: `${section.id}-${section.originalIndex}`,
          anchorId: sanitizeAnchorId(`${section.title}-${navIndex + 1}`),
        })),
    [sections]
  );

  const [activeSectionId, setActiveSectionId] = React.useState<string>(
    navigableSections[0]?.navKey || ""
  );
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [desktopMenuCollapsed, setDesktopMenuCollapsed] = React.useState(false);
  const [mobileRailVisible, setMobileRailVisible] = React.useState(
    !forceMobileNav
  );

  React.useEffect(() => {
    loadGuidebookFont(globalStyles.fontFamily);
  }, [globalStyles.fontFamily]);

  React.useEffect(() => {
    setMobileRailVisible(!forceMobileNav);
  }, [forceMobileNav]);

  React.useEffect(() => {
    const firstNavKey = navigableSections[0]?.navKey || "";
    const activeStillExists = navigableSections.some(
      (section) => section.navKey === activeSectionId
    );

    if (!activeStillExists) {
      setActiveSectionId(firstNavKey);
    }
  }, [activeSectionId, navigableSections]);

  React.useEffect(() => {
    if (typeof window === "undefined" || !navigableSections.length) return;

    const resolveActiveSection = () => {
      const visibleSections = navigableSections
        .map((section) => ({
          navKey: section.navKey,
          element: document.getElementById(section.anchorId),
        }))
        .filter(
          (
            section
          ): section is {
            navKey: string;
            element: HTMLElement;
          } => Boolean(section.element)
        );

      if (!visibleSections.length) return;

      const container = scrollContainerRef?.current;
      const scrollOffset = container ? container.scrollTop : window.scrollY;
      if (scrollOffset <= 4) {
        setActiveSectionId((current) =>
          current === visibleSections[0].navKey ? current : visibleSections[0].navKey
        );
        return;
      }

      const containerRect = container?.getBoundingClientRect();
      const threshold = container
        ? containerRect!.top + containerRect!.height / 2
        : window.innerHeight / 2;

      let nextActiveKey = visibleSections[0].navKey;
      visibleSections.forEach((section) => {
        if (section.element.getBoundingClientRect().top <= threshold) {
          nextActiveKey = section.navKey;
        }
      });

      setActiveSectionId((current) =>
        current === nextActiveKey ? current : nextActiveKey
      );
    };

    let rafId = 0;
    const onScroll = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        resolveActiveSection();
      });
    };

    const container = scrollContainerRef?.current;
    const scrollTarget: HTMLElement | Window = container || window;

    resolveActiveSection();
    scrollTarget.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });

    return () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      scrollTarget.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [navigableSections, scrollContainerRef]);

  const activeSectionTitle =
    navigableSections.find((section) => section.navKey === activeSectionId)
      ?.title || "Guidebook Menu";

  const renderSectionIcon = (
    section: (typeof navigableSections)[number],
    size = 18
  ) =>
    React.createElement(
      SECTION_ICON_BY_TYPE[
        (section.iconType || "rules") as keyof typeof SECTION_ICON_BY_TYPE
      ] || FileText,
      { size }
    );

  const handleNavClick = (anchorId: string, sectionNavId: string) => {
    setActiveSectionId(sectionNavId);
    setMobileMenuOpen(false);

    const container = scrollContainerRef?.current;
    if (!container) {
      document.getElementById(anchorId)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      return;
    }

    const target =
      container.querySelector<HTMLElement>(`#${anchorId}`) ||
      document.getElementById(anchorId);

    if (!target) return;

    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const nextTop = container.scrollTop + (targetRect.top - containerRect.top);

    container.scrollTo({
      top: Math.max(0, nextTop),
      behavior: "smooth",
    });
  };

  if (!sections.length) {
    return (
      <S.ViewerShell backgroundColor={globalStyles.backgroundColor}>
        <S.EmptyState>No sections added yet.</S.EmptyState>
      </S.ViewerShell>
    );
  }

  return (
    <S.ViewerShell backgroundColor={globalStyles.backgroundColor}>
      <S.Hero backgroundColor={globalStyles.backgroundColor}>
        <S.HeroTitle
          fontColor={globalStyles.fontColor}
          fontFamily={globalStyles.fontFamily}
          fontSize={globalStyles.headingFontSize}
          fontWeight={globalStyles.headingFontWeight}
        >
          {title}
        </S.HeroTitle>
        <S.HeroMeta fontColor={globalStyles.fontColor} fontFamily={globalStyles.fontFamily}>
          {contextLabel || "Use the navigation to jump directly to the information you need."}
        </S.HeroMeta>
      </S.Hero>

      {forceMobileNav && (
        <S.MobileRailToggleRow>
          <S.MobileRailToggleButton
            type="button"
            onClick={() => setMobileRailVisible((visible) => !visible)}
            aria-expanded={mobileRailVisible}
          >
            <Menu size={18} />
            Menu
          </S.MobileRailToggleButton>
        </S.MobileRailToggleRow>
      )}

      <S.ViewerLayout
        forceMobile={forceMobileNav}
        mobileRailVisible={mobileRailVisible}
        desktopCollapsed={desktopMenuCollapsed}
      >
        <S.StickyNav
          forceMobile={forceMobileNav}
          backgroundColor={globalStyles.backgroundColor}
          collapsed={desktopMenuCollapsed}
        >
          <S.DesktopNavToggle
            type="button"
            onClick={() => setDesktopMenuCollapsed((collapsed) => !collapsed)}
            aria-expanded={!desktopMenuCollapsed}
            title="Guidebook Menu"
            fontColor={globalStyles.fontColor}
            fontFamily={globalStyles.fontFamily}
            collapsed={desktopMenuCollapsed}
          >
            {!desktopMenuCollapsed && <span>Guidebook Menu</span>}
            <Menu size={18} />
          </S.DesktopNavToggle>

          {desktopMenuCollapsed ? (
            <S.MobileNavList rail open>
              {navigableSections.map((section) => (
                <S.MobileIconButton
                  key={`${section.navKey}-desktop-rail`}
                  active={activeSectionId === section.navKey}
                  fontColor={globalStyles.fontColor}
                  type="button"
                  onClick={() => handleNavClick(section.anchorId, section.navKey)}
                  title={section.title}
                >
                  {renderSectionIcon(section)}
                </S.MobileIconButton>
              ))}
            </S.MobileNavList>
          ) : (
            navigableSections.map((section) => (
              <S.NavButton
                key={section.navKey}
                active={activeSectionId === section.navKey}
                fontColor={globalStyles.fontColor}
                fontFamily={globalStyles.fontFamily}
                type="button"
                onClick={() => handleNavClick(section.anchorId, section.navKey)}
              >
                {section.title}
              </S.NavButton>
            ))
          )}
        </S.StickyNav>

        {(!forceMobileNav || mobileRailVisible) && (
          <S.MobileNav
            forceMobile={forceMobileNav}
            backgroundColor={globalStyles.backgroundColor}
          >
            {!forceMobileNav && (
              <S.MobileNavToggle
                type="button"
                open={mobileMenuOpen}
                onClick={() => setMobileMenuOpen((open) => !open)}
                fontColor={globalStyles.fontColor}
                fontFamily={globalStyles.fontFamily}
                aria-expanded={mobileMenuOpen}
              >
                {activeSectionTitle}
              </S.MobileNavToggle>
            )}
            <S.MobileNavList
              rail={forceMobileNav}
              open={forceMobileNav || mobileMenuOpen}
            >
              {navigableSections.map((section) => (
                <S.MobileIconButton
                  key={`${section.navKey}-mobile`}
                  active={activeSectionId === section.navKey}
                  fontColor={globalStyles.fontColor}
                  type="button"
                  onClick={() => handleNavClick(section.anchorId, section.navKey)}
                  title={section.title}
                >
                  {renderSectionIcon(section)}
                </S.MobileIconButton>
              ))}
            </S.MobileNavList>
          </S.MobileNav>
        )}

        <S.ContentColumn>
          {sections.map((section, sectionIndex) => {
            if (section.title === "Divider") {
              return <S.DividerCard key={`${section.id}-${sectionIndex}`} />;
            }

            const anchorId =
              navigableSections.find((item) => item.originalIndex === sectionIndex)?.anchorId ||
              sanitizeAnchorId(section.id);

            const sectionStyleOverrides = mergeWithDefined<GuidebookStyleFormData>(
              sectionStyles[section.id] || {},
              section.styleOverrides
            );
            const mergedStyles = mergeWithDefined<GuidebookStyleFormData>(
              globalStyles,
              sectionStyleOverrides
            );

            return (
              <S.SectionCard
                key={`${section.id}-${sectionIndex}`}
                id={anchorId}
                backgroundColor={mergedStyles.backgroundColor}
              >
                <S.SectionHeading
                  fontColor={mergedStyles.fontColor}
                  fontFamily={mergedStyles.fontFamily}
                  fontSize={mergedStyles.headingFontSize || "22px"}
                  fontWeight={mergedStyles.headingFontWeight || "700"}
                >
                  {section.title}
                </S.SectionHeading>

                <GuidebookTextBlock
                  html={getGuidebookContentHtml(section.content, (item) => resolveShortcodes(item, shortcodeValues), sanitizeHtml)}
                  renderProgressiveContent={(html) => (
                    <S.RichContent
                      fontColor={mergedStyles.fontColor}
                      fontFamily={mergedStyles.fontFamily}
                      fontSize={mergedStyles.textFontSize}
                      fontWeight={mergedStyles.textFontWeight}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
                    />
                  )}
                  mode={section.collapseMode}
                  title={section.title}
                  fontColor={mergedStyles.fontColor}
                  fontFamily={mergedStyles.fontFamily}
                  fontSize={mergedStyles.textFontSize}
                  fontWeight={mergedStyles.textFontWeight}
                >
                {section.content.map((item, idx) => {
                  if (!item) return null;
                  const hasRich = /<[^>]+>/.test(item) || /\{{\s*[^}]+\s*\}}/.test(item);

                  if (hasRich) {
                    const resolvedHtml = sanitizeHtml(
                      resolveShortcodes(item, shortcodeValues)
                    );
                    return (
                      <S.RichContent
                        key={`${section.id}-content-${idx}`}
                        fontColor={mergedStyles.fontColor}
                        fontFamily={mergedStyles.fontFamily}
                        fontSize={mergedStyles.textFontSize}
                        fontWeight={mergedStyles.textFontWeight}
                        dangerouslySetInnerHTML={{ __html: resolvedHtml }}
                      />
                    );
                  }

                  return (
                    <S.PlainText
                      key={`${section.id}-content-${idx}`}
                      fontColor={mergedStyles.fontColor}
                      fontFamily={mergedStyles.fontFamily}
                      fontSize={mergedStyles.textFontSize}
                      fontWeight={mergedStyles.textFontWeight}
                    >
                      {item}
                    </S.PlainText>
                  );
                })}
                </GuidebookTextBlock>

                {!!section.images?.length && (
                  <S.MediaStack>
                    {section.images.map((imageItem, idx) => {
                      const imageData =
                        typeof imageItem === "string"
                          ? { src: imageItem, fallbackSrcs: [] }
                          : imageItem;

                      return (
                        <GuidebookMediaImage
                          key={`${section.id}-image-${idx}`}
                          src={imageData.src}
                          fallbackSrcs={imageData.fallbackSrcs}
                          alt={`${section.title} image ${idx + 1}`}
                        />
                      );
                    })}
                  </S.MediaStack>
                )}

                {!!section.externalVideoUrls?.length && (
                  <S.MediaStack>
                    {section.externalVideoUrls.map((videoUrl, idx) =>
                      isYouTubeEmbedUrl(videoUrl) ? (
                        <S.VideoFrame
                          key={`${section.id}-video-${idx}`}
                          src={videoUrl}
                          title={`${section.title} video ${idx + 1}`}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <S.VideoPlayer
                          key={`${section.id}-video-${idx}`}
                          src={videoUrl}
                          controls
                          preload="metadata"
                          controlsList="nodownload"
                        />
                      )
                    )}
                  </S.MediaStack>
                )}

                {!!section.links?.length && (
                  <S.LinkList>
                    {section.links.map((link, idx) => (
                      <S.LinkCard
                        key={`${section.id}-link-${idx}`}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span>
                          <S.LinkTitle>{link.title}</S.LinkTitle>
                          <S.LinkUrl>{link.url}</S.LinkUrl>
                        </span>
                        <ExternalLink size={16} />
                      </S.LinkCard>
                    ))}
                  </S.LinkList>
                )}

                {!!section.pdfs?.length && (
                  <S.LinkList>
                    {section.pdfs.map((pdf, idx) => (
                      <S.LinkCard
                        key={`${section.id}-pdf-${idx}`}
                        href={pdf.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span>
                          <S.LinkTitle>{pdf.originalName}</S.LinkTitle>
                          <S.LinkUrl>Open PDF</S.LinkUrl>
                        </span>
                        <ExternalLink size={16} />
                      </S.LinkCard>
                    ))}
                  </S.LinkList>
                )}
              </S.SectionCard>
            );
          })}
        </S.ContentColumn>
      </S.ViewerLayout>
    </S.ViewerShell>
  );
};

export default GuidebookViewer;
